/**
 * Atrium Desktop client detection + deep-link helpers.
 *
 * The Atrium app ships as a web app (atrium.run) and as an Electron desktop
 * shell (built via ToDesktop). When flows leave the app for an external
 * service — Supabase OAuth providers, Stripe Checkout, Google OAuth — the
 * external service redirects back over HTTPS, which lands in the system
 * browser rather than the desktop app. We bridge the gap by:
 *   1. Telling the server "this request came from desktop" via a header or
 *      query param.
 *   2. Sending the server's redirects through `/desktop-bridge/[flow]`, a
 *      tiny interstitial that fires an `atrium://` deep link to hand control
 *      back to the desktop shell.
 *
 * Nothing here changes runtime behavior on its own — these helpers are
 * scaffolding for the ToDesktop wrapper and the auth/Stripe wiring that
 * follow in subsequent PRs.
 */

const DESKTOP_CLIENT_HEADER = 'x-atrium-client'
const DESKTOP_CLIENT_QUERY_KEY = 'client'
const DESKTOP_CLIENT_VALUE = 'desktop'

/**
 * Custom protocol scheme registered by the Atrium Desktop shell.
 * Configured in the ToDesktop Builder app settings.
 */
export const DESKTOP_PROTOCOL = 'atrium'

/**
 * Server-side: is this request coming from the Atrium Desktop client?
 *
 * The Electron preload script injects `X-Atrium-Client: desktop` on every
 * outbound `fetch`. For redirect-driven flows (Stripe success_url, OAuth
 * callbacks) where the request comes back via the user's system browser
 * and headers can't be carried, the desktop shell appends `?client=desktop`
 * to the URL it opens, so we also accept the query param as a signal.
 */
export function isDesktopRequest(req: Request): boolean {
  if (req.headers.get(DESKTOP_CLIENT_HEADER) === DESKTOP_CLIENT_VALUE) return true
  try {
    const url = new URL(req.url)
    if (url.searchParams.get(DESKTOP_CLIENT_QUERY_KEY) === DESKTOP_CLIENT_VALUE) return true
  } catch {
    // Malformed request URL — treat as non-desktop.
  }
  return false
}

/**
 * Client-side: is the current page running inside the Atrium Desktop shell?
 *
 * Always returns false on the server. Safe to call during render.
 */
export function isDesktopBrowser(): boolean {
  if (typeof window === 'undefined') return false
  const w = window as DesktopWindow
  if (w.__ATRIUM_DESKTOP__ === true) return true
  if (typeof navigator !== 'undefined' && navigator.userAgent.includes('AtriumDesktop')) return true
  if (typeof w.location !== 'undefined') {
    const params = new URLSearchParams(w.location.search)
    if (params.get(DESKTOP_CLIENT_QUERY_KEY) === DESKTOP_CLIENT_VALUE) return true
  }
  return false
}

type DesktopWindow = Window & { __ATRIUM_DESKTOP__?: boolean }

/**
 * Build an `atrium://` deep link.
 *
 * The flow name maps 1:1 to a route inside the desktop shell — e.g.
 * `buildDesktopDeepLink('checkout', { session_id: 'cs_test_123' })`
 * → `atrium://checkout?session_id=cs_test_123`.
 *
 * Throws if `flow` is empty or contains characters outside `[A-Za-z0-9_-]`,
 * since the bridge page renders user-controlled `flow` values from the URL.
 */
export function buildDesktopDeepLink(
  flow: string,
  params?: Record<string, string | null | undefined>,
): string {
  const safeFlow = flow.replace(/[^A-Za-z0-9_-]/g, '')
  if (!safeFlow) {
    throw new Error(`Invalid desktop deep-link flow: ${JSON.stringify(flow)}`)
  }
  const url = new URL(`${DESKTOP_PROTOCOL}://${safeFlow}`)
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value != null) url.searchParams.set(key, value)
    }
  }
  return url.toString()
}

/**
 * `/^[a-z][a-z0-9-]*$/` — flow names allowed in the `/desktop-bridge/[flow]`
 * route. Mirrors the sanitization in `buildDesktopDeepLink` but stricter
 * (lowercase, must start with a letter). Shared so the bridge page and
 * call sites stay in sync.
 */
export const VALID_FLOW_PATTERN = /^[a-z][a-z0-9-]*$/

export function isValidDesktopFlow(flow: string): boolean {
  return VALID_FLOW_PATTERN.test(flow)
}

/**
 * Append `?client=desktop` (or `&client=desktop`) to a URL when the page is
 * running inside the Atrium Desktop shell. Used for client-side flows
 * (Supabase OAuth, magic links) where the `redirectTo` URL is consumed by
 * an external service and comes back to the system browser — the query
 * param is how the server learns the request originated from desktop.
 *
 * Returns the URL unchanged on the server or in a regular browser.
 */
export function withDesktopClientFlag(url: string): string {
  if (!isDesktopBrowser()) return url
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}${DESKTOP_CLIENT_QUERY_KEY}=${DESKTOP_CLIENT_VALUE}`
}

/**
 * Build the absolute `/desktop-bridge/[flow]` URL used as a `success_url`
 * or `return_url` for external services (Stripe, OAuth providers) when
 * the original request came from the desktop client.
 *
 * `webFallback` is a path (must start with `/`) where browser-only users
 * land after the bridge times out. `templateTokens` is for Stripe-style
 * placeholders like `{CHECKOUT_SESSION_ID}` which must remain unencoded
 * so Stripe substitutes them server-side.
 */
export function buildBridgeUrl(args: {
  origin: string
  flow: string
  webFallback: string
  params?: Record<string, string>
  templateTokens?: Record<string, string>
}): string {
  if (!isValidDesktopFlow(args.flow)) {
    throw new Error(`Invalid desktop bridge flow: ${JSON.stringify(args.flow)}`)
  }
  if (!args.webFallback.startsWith('/')) {
    throw new Error(`webFallback must be a path starting with "/", got ${JSON.stringify(args.webFallback)}`)
  }
  const params = new URLSearchParams()
  params.set('_fallback', args.webFallback)
  if (args.params) {
    for (const [key, value] of Object.entries(args.params)) params.set(key, value)
  }
  let qs = params.toString()
  if (args.templateTokens) {
    const tokenParts = Object.entries(args.templateTokens).map(
      ([key, value]) => `${encodeURIComponent(key)}=${value}`,
    )
    qs = qs ? `${qs}&${tokenParts.join('&')}` : tokenParts.join('&')
  }
  return `${args.origin}/desktop-bridge/${args.flow}?${qs}`
}
